import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

interface UserProfile {
  id: string;
  email: string;
  org_id: string;
  role: "org_admin" | "facility_admin" | "staff";
}

interface UseAuthReturn {
  user: UserProfile | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook for handling user authentication and profile data
 *
 * @returns Object containing user profile, loading state, and error
 *
 * @example
 * const { user, loading, error } = useAuth();
 * if (loading) return <LoadingSpinner />;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!user) return <div>Not authenticated</div>;
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      const { data: userProfile, error: profileError } = await supabase
        .from("users")
        .select("id, org_id, role")
        .eq("id", authUser.id)
        .single();

      if (profileError) throw profileError;

      if (!userProfile) {
        setUser(null);
        setLoading(false);
        return;
      }

      setUser((prevUser) => {
        const newUser = {
          id: userProfile.id,
          email: authUser.email!,
          org_id: userProfile.org_id,
          role: userProfile.role,
        };

        // Only update if user has actually changed
        if (
          prevUser?.id === newUser.id &&
          prevUser?.org_id === newUser.org_id &&
          prevUser?.role === newUser.role
        ) {
          return prevUser;
        }

        return newUser;
      });
    } catch (err) {
      console.error("Error loading user:", err);
      setError(err instanceof Error ? err : new Error("Failed to load user"));
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      loadUser();
    });

    return () => subscription.unsubscribe();
  }, [loadUser]);

  return { user, loading, error };
}
